import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';

import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { HeatmapLayer as DeckHeatmapLayer } from '@deck.gl/aggregation-layers';
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';

const mapContainerStyle = { width: '100%', height: '400px' };
const center = { lat: 16.5062, lng: 80.6480 }; // Vijayawada

// Helper: generate random points around a city
const generatePoints = (baseLat, baseLng, count, namePrefix) => {
  const points = [];
  for (let i = 0; i < count; i++) {
    const latOffset = (Math.random() - 0.5) * 0.05; // ~5km variation
    const lngOffset = (Math.random() - 0.5) * 0.05;
    points.push({
      lat: baseLat + latOffset,
      lng: baseLng + lngOffset,
      name: `${namePrefix} ${i + 1}`
    });
  }
  return points;
};

// Generate realistic clustered data
const suppliers = [
  ...generatePoints(16.5062, 80.6480, 3, 'Supplier Vijayawada'),
  ...generatePoints(16.3067, 80.4365, 3, 'Supplier Guntur'),
  ...generatePoints(17.3850, 78.4867, 4, 'Supplier Hyderabad')
];

const consumers = [
  ...generatePoints(16.5062, 80.6480, 3, 'Consumer Vijayawada'),
  ...generatePoints(16.3067, 80.4365, 3, 'Consumer Guntur'),
  ...generatePoints(17.3850, 78.4867, 4, 'Consumer Hyderabad')
];

// Heatmap data
const heatmapData = [
  ...suppliers.map(p => ({ ...p, weight: 2 })),
  ...consumers.map(p => ({ ...p, weight: 1 }))
];

export default function Dashboard() {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyBRUP8Qe2tPjC8vExjseQ0t3YD9qp3Ok30', // Replace
    libraries: []
  });

  const mapRef = useRef(null);
  const overlayRef = useRef(null);

  const createHeatmapLayer = useCallback(() => {
    return new DeckHeatmapLayer({
      id: 'heatmap-layer',
      data: heatmapData,
      getPosition: d => [d.lng, d.lat],
      getWeight: d => d.weight || 1,
      radiusPixels: 60,
      intensity: 1,
      threshold: 0.03,
      opacity: 0.35
    });
  }, []);

  const createSupplierLayer = useCallback(() => {
    return new ScatterplotLayer({
      id: 'suppliers-layer',
      data: suppliers,
      getPosition: d => [d.lng, d.lat],
      getRadius: 500,
      getFillColor: [255, 0, 0],
      pickable: true,
      onClick: info => setSelectedMarker(info.object)
    });
  }, []);

  const createConsumerLayer = useCallback(() => {
    return new ScatterplotLayer({
      id: 'consumers-layer',
      data: consumers,
      getPosition: d => [d.lng, d.lat],
      getRadius: 500,
      getFillColor: [0, 0, 255],
      pickable: true,
      onClick: info => setSelectedMarker(info.object)
    });
  }, []);

  const createLabelLayer = useCallback(() => {
    return new TextLayer({
      id: 'labels-layer',
      data: [...suppliers, ...consumers],
      getPosition: d => [d.lng, d.lat],
      getText: d => d.name,
      getSize: 14,
      getColor: [0, 0, 0],
      getTextAnchor: 'start',
      getAlignmentBaseline: 'center'
    });
  }, []);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;

    const overlay = new GoogleMapsOverlay({
      layers: [
        createHeatmapLayer(),
        createSupplierLayer(),
        createConsumerLayer(),
        createLabelLayer()
      ]
    });

    overlay.setMap(map);
    overlayRef.current = overlay;
  }, [createHeatmapLayer, createSupplierLayer, createConsumerLayer, createLabelLayer]);

  useEffect(() => {
    if (overlayRef.current) {
      overlayRef.current.setProps({
        layers: [
          createHeatmapLayer(),
          createSupplierLayer(),
          createConsumerLayer(),
          createLabelLayer()
        ]
      });
    }
  }, [createHeatmapLayer, createSupplierLayer, createConsumerLayer, createLabelLayer]);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  // KPI data
  const kpiData = [
    { label: 'Total Leads', value: 1000, sub: '+8% vs last period' },
    { label: 'Total RFQ', value: 800 },
    { label: 'RFQ Response Rate', value: '92%', sub: 'Avg 2.5h to respond' },
    { label: 'Product Page Performance', value: '+8%', sub: 'vs competitors' },
    { label: 'Product Video Engagement', value: '3.2k views', sub: '60% watch-through' },
    { label: 'Conversion Rate', value: '18%', sub: 'Rep comparison' }
  ];

  const funnelData = [
    { stage: 'Leads', value: 1000 },
    { stage: 'RFQ', value: 800 },
    { stage: 'Response Sent', value: 600 },
    { stage: 'Shortlisted', value: 300 },
    { stage: 'Order', value: 150 }
  ];

  return (
    <div className="p-6 bg-gray-100 min-h-screen space-y-6">
      {/* KPIs */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h2 className="text-2xl font-bold mb-4">Dashboard KPIs</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {kpiData.map((kpi, idx) => (
            <div key={idx} className="p-4 bg-gray-50 rounded-lg shadow-sm">
              <p className="font-semibold">{kpi.label}</p>
              <p className="text-xl font-bold">{kpi.value}</p>
              {kpi.sub && <p className="text-xs text-gray-500">{kpi.sub}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h2 className="text-xl font-bold mb-4">Conversion Funnel</h2>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={funnelData}>
            <XAxis dataKey="stage" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="#4F46E5" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Map with heatmap and markers */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h2 className="text-xl font-bold mb-4">Supplier & Consumer Heat Map</h2>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={8}
          onLoad={handleMapLoad}
        />
        {selectedMarker && (
          <InfoWindow
            position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div>
              <strong>{selectedMarker.name}</strong>
            </div>
          </InfoWindow>
        )}
      </div>
    </div>
  );
}
